import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../Store';
import { axiosInstance } from '../../../Authentication/AxiosInterceptor';
import { IDropDownListItem } from '../../../Types/DropDownList/IDropDownListItem';
import {ClientRolePermission, ClientRolePermissionType} from "../../../Types/Roles/RolePermission";
import type { LegacyBlazorNavVisibility } from "../../../Types/LegacyBlazorNavVisibility";
import { NavbarAccess, NavbarAccessDeniedReason } from "../../../Types/Navbar/NavbarAccess";
import { isMiaCoachEnabled } from "../../../Helpers/miaCoachEnabled";

export type clientOption = {
    id: string;
    name: string;
};

export interface ClientState {
    clientOptions: clientOption[];
    activeClient?: clientOption;
    clientRolePermission?: ClientRolePermission;
    hasThirdPartySubsets?: boolean;
    canMandateToSubsets?: boolean;
    aiPowered?: boolean;
    /** Mia Coach under Evaluate answers. Undefined until bootstrap; treated as on unless explicitly false. */
    miaCoachEnabled?: boolean;
    gen3MesmaEnabled?: boolean;
    /** Innovate redirect enabled for this client (CPanel Gen3RedirectEnable). Controls legacy navbar links. */
    gen3RedirectEnabled?: boolean;
    /** Gen 3 Library mandated for this client. Undefined until bootstrap; false keeps the classic Library. */
    libraryGen3Enabled?: boolean;
    developMesmaEnabled?: boolean;
    navbarAccess?: NavbarAccess;
    /** Classic (Blazor) drawer link visibility; null if the visibility request failed or returned nothing useful. */
    legacyBlazorNav: LegacyBlazorNavVisibility | null;
}

const initialState: ClientState = {
    clientOptions: [],
    activeClient: undefined,
    clientRolePermission: undefined,
    hasThirdPartySubsets: false,
    aiPowered: undefined, // Will be set from API call
    miaCoachEnabled: undefined,
    gen3MesmaEnabled: undefined, // Will be set from API call
    gen3RedirectEnabled: undefined,
    libraryGen3Enabled: undefined,
    developMesmaEnabled: undefined, // Will be set from API call
    navbarAccess: undefined,
    legacyBlazorNav: null,
};

export const loadClientOptions = createAsyncThunk(
  'client/loadClientOptions',
  async () => {
    const response = await axiosInstance.get<clientOption[]>("/GetAvailableClientNames");
    return response.data;
  }
);

interface SetClientParams {
    userSub: string;
    clientId: string;
}

/** Aggregated bootstrap payload returned by `GetInnovateClientSetup`. */
type InnovateClientSetupResponse = {
    clientRolePermission: ClientRolePermission;
    clientSubsets: { partners: any[]; departments: any[] };
    canMandateToSubsets: boolean;
    aiPowered: boolean;
    miaCoachEnabled: boolean;
    gen3Enabled: boolean;
    gen3RedirectEnabled: boolean;
    libraryGen3Enabled: boolean;
    developEnabled: boolean;
    navbarAccess: NavbarAccess;
    legacyBlazorNav: LegacyBlazorNavVisibility | null;
};

export const setClient = createAsyncThunk<
    { clientId: string; clientRolePermission: ClientRolePermission; hasThirdPartySubsets: boolean; canMandate: boolean; aiPowered: boolean; miaCoachEnabled: boolean; gen3Enabled: boolean; gen3RedirectEnabled: boolean; libraryGen3Enabled: boolean; developEnabled: boolean; navbarAccess: NavbarAccess; legacyBlazorNav: LegacyBlazorNavVisibility | null },
    SetClientParams
>(
    'client/setClient',
    async ({ clientId }) => {
        if (!clientId || clientId === 'undefined' || clientId === 'null') {
            throw new Error(`Invalid client ID: ${clientId}`);
        }

        // Single aggregated bootstrap call. Server-side fans out role permissions, subsets,
        // mandate flag, ai/gen3/develop flags, navbar access, and legacy nav visibility in
        // parallel (see InnovateClientSetup.cs). Legacy nav visibility is best-effort there
        // and falls back to null without failing the bootstrap.
        const { data } = await axiosInstance.get<InnovateClientSetupResponse>(`/GetInnovateClientSetup/${clientId}`);

        const hasThirdPartySubsets =
            data.clientSubsets.partners.length > 0 || data.clientSubsets.departments.length > 0;

        return {
            clientId,
            clientRolePermission: data.clientRolePermission,
            hasThirdPartySubsets,
            canMandate: data.canMandateToSubsets,
            aiPowered: data.aiPowered,
            miaCoachEnabled: data.miaCoachEnabled,
            gen3Enabled: data.gen3Enabled,
            gen3RedirectEnabled: data.gen3RedirectEnabled,
            libraryGen3Enabled: data.libraryGen3Enabled === true,
            developEnabled: data.developEnabled,
            navbarAccess: data.navbarAccess,
            legacyBlazorNav: data.legacyBlazorNav,
        };
    }
);

export const ClientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    setClientOptions(state, action: PayloadAction<clientOption[]>) {
      state.clientOptions = action.payload;
    },
    setLibraryGen3Enabled(state, action: PayloadAction<boolean>) {
      state.libraryGen3Enabled = action.payload;
    },
    applyClientPreferences(state, action: PayloadAction<{ aiPowered?: boolean; miaCoachEnabled?: boolean }>) {
      if (action.payload.aiPowered !== undefined) {
        state.aiPowered = action.payload.aiPowered;
      }
      if (action.payload.miaCoachEnabled !== undefined) {
        state.miaCoachEnabled = action.payload.miaCoachEnabled;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadClientOptions.fulfilled, (state, action) => {
      state.clientOptions = action.payload;
      console.log("Client options loaded:", action.payload);
    });

        builder.addCase(setClient.pending, (state) => {
        state.legacyBlazorNav = null;
        state.libraryGen3Enabled = undefined;
    });

        builder.addCase(setClient.fulfilled, (state, action) => {
        const clientOption = state.clientOptions.find(client => client.id === action.payload.clientId);
        state.activeClient = clientOption;
        state.clientRolePermission = action.payload.clientRolePermission;
        state.hasThirdPartySubsets = action.payload.hasThirdPartySubsets;
        state.canMandateToSubsets = action.payload.canMandate;
        state.aiPowered = action.payload.aiPowered;
        state.miaCoachEnabled = action.payload.miaCoachEnabled;
        state.gen3MesmaEnabled = action.payload.gen3Enabled;
        state.gen3RedirectEnabled = action.payload.gen3RedirectEnabled;
        state.libraryGen3Enabled = action.payload.libraryGen3Enabled;
        state.developMesmaEnabled = action.payload.developEnabled;
        state.navbarAccess = action.payload.navbarAccess;
        state.legacyBlazorNav = action.payload.legacyBlazorNav;
        console.log("can mandate:", state.canMandateToSubsets);
        console.log("ai powered:", state.aiPowered);
    });
  },
});

export const { setClientOptions, applyClientPreferences, setLibraryGen3Enabled } = ClientSlice.actions;
export const getClient = (state: RootState) => state.client.activeClient;
export const getClientOptions = (state: RootState) => state.client.clientOptions;
export const getClientOptionsDropdown = (state: RootState): IDropDownListItem[] =>
  state.client.clientOptions.map(client => ({
    text: client.name,
    colour: undefined,
    textColour: undefined,
    value: client.id,
  }));
export const getClientRolePermission = (state: RootState) => state.client.clientRolePermission;
export const getClientHasThirdPartySubsets = (state: RootState) => state.client.hasThirdPartySubsets;
export const getClientCanMandateToSubsets = (state: RootState) => state.client.canMandateToSubsets;
export const getAiPowered = (state: RootState) => state.client.aiPowered === true;
/** Coach is on only when AI is on, and stays on when the preference has not been stored yet. */
export const getMiaCoachEnabled = (state: RootState) =>
    isMiaCoachEnabled(state.client.aiPowered, state.client.miaCoachEnabled);
export const getGen3Enabled = (state: RootState) => state.client.gen3MesmaEnabled;
export const getGen3RedirectEnabled = (state: RootState) => state.client.gen3RedirectEnabled === true;
export const getLibraryGen3Enabled = (state: RootState) => state.client.libraryGen3Enabled;
export const getDevelopEnabled = (state: RootState) => state.client.developMesmaEnabled;
export const getLegacyBlazorNavVisibility = (state: RootState) => state.client.legacyBlazorNav;
export const getIsClientAdmin = (state: RootState) => {
    const rolePermission = state.client.clientRolePermission;
    // Check if user has an admin-level role type
    return rolePermission?.permissionType === ClientRolePermissionType.SuperAdmin ||
           rolePermission?.permissionType === ClientRolePermissionType.AdvancedAdmin ||
           rolePermission?.permissionType === ClientRolePermissionType.ClientAdmin ||
           rolePermission?.permissionType === ClientRolePermissionType.MesmaAdmin;
};

export const getIsMesmaAdmin = (state: RootState) =>
    state.client.clientRolePermission?.permissionType === ClientRolePermissionType.MesmaAdmin;

export const getCanAccessEvaluate = (state: RootState): boolean =>
    state.client.navbarAccess?.canAccessEvaluate === true;

export const getCanAccessTrack = (state: RootState): boolean =>
    state.client.navbarAccess?.canAccessTrack === true;

export const getCanAccessDevelop = (state: RootState): boolean =>
    state.client.navbarAccess?.canAccessDevelop === true;

export const getEvaluateDeniedReason = (state: RootState): NavbarAccessDeniedReason =>
    state.client.navbarAccess?.evaluateDeniedReason ?? NavbarAccessDeniedReason.None;

export const getTrackDeniedReason = (state: RootState): NavbarAccessDeniedReason =>
    state.client.navbarAccess?.trackDeniedReason ?? NavbarAccessDeniedReason.None;

export const getDevelopDeniedReason = (state: RootState): NavbarAccessDeniedReason =>
    state.client.navbarAccess?.developDeniedReason ?? NavbarAccessDeniedReason.None;

export default ClientSlice.reducer;
